import { Injectable } from '@angular/core';
import { SiteModel } from '../models/site';
import { AccountModel } from '../models/account';
import {environment} from 'src/environments/environment';
import { Device, LanguageTag } from '@capacitor/device';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root'
})
export class AppService {

  // App Region
  region: string = '';

  // App API URL
  api_url: string = '';

  auth: {
    email: string,
    password: string
  } = {
    email: '',
    password: ''
  };

  private _isAuthenticated = false;
  private _isSystemAdmin = false;

  account: AccountModel = new AccountModel();

  site: SiteModel = new SiteModel();

  constructor(
    private storage: StorageService
  ) { }

  isAuthenticated(_isAuthenticated?: boolean) {
    if ( typeof _isAuthenticated !== 'undefined' ) {
      this._isAuthenticated = _isAuthenticated;
    }
    return this._isAuthenticated;
  }

  isSystemAdmin(_isSystemAdmin?: boolean) {
    if ( typeof _isSystemAdmin !== 'undefined' ) {
      this._isSystemAdmin = _isSystemAdmin;
    }
    return this._isSystemAdmin;
  }

  /**
   * Retrieves the 'Region' value from the storage asynchronously.
   *
   * @return {Promise<any>} A promise that resolves to the value of 'Region' from the storage.
   */
  getRegion(): string {
    this.region = this.storage.get('Region');
    return this.region;
  }

  /**
   * Retrieves the API URL based on the country ISO code stored in the local storage.
   * If no specific country ISO code is found, a default value is used.
   * The API URL is dynamically determined for different countries (e.g., 'ANZ', 'UK').
   *
   * @return {Promise<string>} A promise resolving to the appropriate API URL as a string.
   */
  prepareAPIURL(_region?: string): string {
    // Get country ISO code from storage.
    const region: string = _region || (this.getRegion() || 'ANZ');
    // Define the default URL.
    let url: string = environment.API_URL_ANZ;
    // Check if the country is UK.
    if ( region === 'UK' ) {
      url = environment.API_URL_UK;
    }
    // Store the API URL.
    this.api_url = url;
    return url;
  }

  /**
   * Retrieves the country code from the device's language tag.
   *
   * This method determines the country of the device based on its language settings.
   * If the language tag indicates the United Kingdom (ends with '-GB'), it will return 'UK'.
   * Otherwise, it defaults to returning 'ANZ'.
   *
   * @return {Promise<'UK' | 'ANZ'>} A promise that resolves to 'UK' if the device is in the United Kingdom, or 'ANZ' for all other regions.
   */
  async getCountryFromDevice(): Promise<'UK' | 'ANZ'> {
    try {
      const languageTag: LanguageTag = await Device.getLanguageTag();
      // Check if the tag ends with the country code for the UK.
      if ( languageTag.value.endsWith('-GB') ) {
        return 'UK';
      }
    } catch (error) {
      return;
    }
    // Default to ANZ for all other regions.
    return 'ANZ';
  }

  /**
   * Retrieves the country code by the user's IP address using an external API.
   * Converts 'GB' to 'UK' and defaults other country codes to 'ANZ'.
   *
   * @return {Promise<string>} A promise resolving to the country code as a string ('UK' or 'ANZ').
   */
  async getCountryByIP(): Promise<string> {
    const token: string = environment.IPAPI_TOKEN;
    try {
      const response: Response = await fetch('https://ipapi.co/json', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data: any = await response.json();
      const country: string = data.country_code;
      if ( country == 'GB' ) {
        return 'UK';
      } else {
        return 'ANZ';
      }
    } catch (error) {
      return;
    }
  }

  /**
   * Retrieves the complete endpoint URL based on the country ISO code.
   *
   * @param {string} endpoint - The specific endpoint path to append to the base URL.
   * @return {Promise<string>} A promise that resolves to the complete URL.
   */
  getAPIEndpointURL(endpoint: string): string {
    return this.api_url + endpoint;
  }
}
